/* eslint-disable react-hooks/exhaustive-deps */
/** @jsxImportSource @emotion/react */
import 'twin.macro';
import tw from 'twin.macro';

import { CollapsibleList } from '@rmwc/list';
import { Ripple } from '@rmwc/ripple';
import { ReactComponent as VisibilityOff } from 'assets/Icons/VisibilityOff.svg';
import { ReactComponent as VisibilityOn } from 'assets/Icons/VisibilityOn.svg';
import { ReactComponent as AddCircle } from 'assets/Icons/add_circle.svg';
import { ReactComponent as ExpandCircle } from 'assets/Icons/expand_circle.svg';
import { ReactComponent as VerifiedSvg } from 'assets/Icons/verified.svg';
import { ReactComponent as SimplecastLogo } from 'assets/Images/SimplecastByAdsWizz-Logo-Primary.svg';
import { CollectionDropdown } from 'components/shared/CollectionDropdown/CollectionDropdown';
import { ConfirmModal } from 'components/shared/ConfirmModal/ConfirmModal';
import { Hint } from 'components/shared/Hint';
import { QuestionHint } from 'components/shared/QuestionHint';
import { Switcher } from 'components/shared/Switcher';
import { debounce, cloneDeep, isEmpty, isNil } from 'lodash';
import React, { ReactElement, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToggle } from 'react-use';
import { SimplecastService } from 'services/simplecast.service';
import {
  removeSimplecastIntegration,
  updateSingleSimplecastIntegration,
} from 'slices/simplecast.slice';
import { tierSelector } from 'slices/payment.slice';
import { toSimplecastProgramOptions } from 'utils/adapter.util';
import { CollectionOptionEnum, HTTPStatus, ModalOptions } from 'utils/enum';
import { ProgramIdOption, SimplecastIntegrationOption } from 'utils/models';
import { customToast } from 'utils/toast.util';
import { useState } from 'react';
import {
  customEnableSwitcherLabelSimplecastCss,
  customIconCss,
  customLoading,
  customTooltipCss,
} from '../Integrations/IntegrationItem';
import { SimplecastIntegrationFilter } from './SimplecastIntegrationFilter';

type Props = {
  integration: SimplecastIntegrationOption;
};

export const SimplecastIntegrationItem = ({ integration }: Props) => {
  const dispatch = useDispatch();
  const confirmRemoveModalRef = useRef<any>(null);

  const tier = useSelector(tierSelector);
  const notSubscriber = !tier.isSubscriber;

  const [shouldShowApiKey, toggleShowApiKey] = useToggle(false);
  const [isOpenFilter, toggleOpenFilter] = useToggle(false);
  const [isFetchingPing, toggleFetchingPing] = useToggle(false);
  const [isValidApiKey, toggleValidApiKey] = useToggle(false);
  const [listProgramId, setListProgramId] = useState<ProgramIdOption[]>([]);
  const [checkedSavedApiKey, toggleCheckedSavedApiKey] = useToggle(false);

  useEffect(() => {
    const savedApiKey = integration?.apiKey?.trim();

    if (isNil(savedApiKey) || isEmpty(savedApiKey) || checkedSavedApiKey)
      return;

    validateKey(savedApiKey);
    toggleCheckedSavedApiKey(true);
  }, [integration?.apiKey]);

  const handleGetProgramId = async (apiKey: string) => {
    try {
      const res = await SimplecastService.getProgramIds(apiKey);

      if (res.status === HTTPStatus.SUCCESS) {
        toggleValidApiKey(true);

        const programs = Array.isArray(res.data) ? res.data : [];

        if (!isEmpty(programs)) {
          setListProgramId(toSimplecastProgramOptions(programs));
        }
      }
    } catch (err) {
      console.log(err);
      setListProgramId([]);
      toggleValidApiKey(false);
    } finally {
      toggleFetchingPing(false);
    }
  };

  const validateKey = async (key: string) => {
    toggleFetchingPing(true);
    await handleGetProgramId(key);
  };

  const validateApiKeyAsync = async (value: string) => {
    toggleFetchingPing(true);
    toggleValidApiKey(false);
    await handleGetProgramId(value);
  };

  const debounceAuthorizeKey = useCallback(
    debounce(validateApiKeyAsync, 300),
    [],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e?.target?.value;

    const clonedIntegration = cloneDeep(integration);
    clonedIntegration.apiKey = value;

    clonedIntegration.filter.programFilters =
      clonedIntegration.filter.programFilters.map(() => ({
        programId: '',
        programName: '',
        rssFeedUrl: '',
      }));

    dispatch(updateSingleSimplecastIntegration(clonedIntegration));

    if (isEmpty(value?.trim())) {
      toggleValidApiKey(false);
      setListProgramId([]);
      toggleFetchingPing(false);
      return;
    }

    debounceAuthorizeKey(value);
  };

  const handleToggleOptions = (
    optionLabel: keyof SimplecastIntegrationOption,
  ) => {
    const isChecked = !integration[optionLabel];

    dispatch(
      updateSingleSimplecastIntegration({
        ...integration,
        [optionLabel]: isChecked,
      }),
    );
  };

  const handleRemoveIntegration = async () => {
    const result = await confirmRemoveModalRef.current?.show();

    if (result !== ModalOptions.YES) return;

    dispatch(removeSimplecastIntegration({ integrationId: integration?.id }));
    customToast.success('Removed');
  };

  const renderValidationMsg = (): ReactElement => {
    if (isEmpty(integration?.apiKey)) return <></>;

    if (isFetchingPing)
      return (
        <small tw="text-10 text-sonnant-orange">Validating API Key...</small>
      );

    if (isValidApiKey)
      return (
        <small tw="flex gap-1.5 items-center text-10 text-sonnant-green font-medium">
          Valid API Key
          <VerifiedSvg tw="zoom[0.9]" />
        </small>
      );

    return (
      <small tw="text-10 text-sonnant-red">
        Invalid API Key. Please check and try again!
      </small>
    );
  };

  return (
    <div tw="flex flex-row py-6 w-full">
      <div tw="min-w-[20rem] w-[50rem] p-[5rem]">
        <SimplecastLogo tw="w-[95%]" aria-label="Simplecast by AdsWizz" />
      </div>

      <div tw="flex flex-col w-full pl-[3rem]">
        <div tw="flex w-full items-center">
          <div tw="min-w-[50%]">
            <div tw="flex flex-col pb-6">
              <div tw="flex flex-row items-center">
                <label tw="flex flex-nowrap mr-3 text-16">API Key</label>
                <QuestionHint
                  notTransparent
                  hideCloseButton
                  type="information"
                  align="bottomLeft"
                  action="click"
                  text={
                    <div tw="py-1">
                      <p>
                        To enable Sonnant to access and write metadata back to
                        Simplecast an authentication key is required. Please
                        contact Adswizz support in order to create a key for
                        you. The Simplecast integrate option will only be
                        available for content items that were uploaded from
                        Simplecast. This option is only available to
                        subscription and enterprise customers.
                      </p>
                    </div>
                  }
                  customStyle={customTooltipCss}
                />
              </div>
              <div tw="flex flex-row max-w-[75rem] md-down:(w-[40rem])">
                <div tw="flex-1 relative ">
                  <input
                    tw="flex pr-[8rem]! mb-0"
                    css={[
                      !isFetchingPing &&
                        !isValidApiKey &&
                        !isEmpty(integration?.apiKey) &&
                        tw`(border-red-500 border-width[1px])!`,
                    ]}
                    type={shouldShowApiKey ? 'text' : 'password'}
                    value={integration.apiKey}
                    onChange={handleInputChange}
                    placeholder="**********************************"
                  />

                  {renderValidationMsg()}

                  {isFetchingPing && (
                    <Hint text="Validating API Key">
                      <div css={customLoading} />
                    </Hint>
                  )}

                  <span onClick={toggleShowApiKey}>
                    {shouldShowApiKey ? (
                      <VisibilityOn tw="mt-[1px]" css={customIconCss} />
                    ) : (
                      <VisibilityOff css={customIconCss} />
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <Hint
            text="Not available on free trial and PAYG. Please upgrade to enable."
            enterDelay={0}
            arrow
            disabled={!notSubscriber}
            align="bottom"
          >
            <div css={customEnableSwitcherLabelSimplecastCss}>
              <Switcher
                hasLabel={true}
                checked={integration.isEnabled}
                disabled={notSubscriber}
                color="#00AF7A"
                label="ENABLED"
                onCheckedChange={() => handleToggleOptions('isEnabled')}
              />
            </div>
          </Hint>
        </div>

        <CollapsibleList
          handle={
            <Ripple tw="py-3 pl-2.5 pr-3 rounded" onClick={toggleOpenFilter}>
              <div tw="inline-flex space-x-2.5 items-center cursor-pointer">
                {isOpenFilter ? (
                  <ExpandCircle fill="#7f8090" />
                ) : (
                  <AddCircle fill="#7f8090" />
                )}
                <div tw="flex flex-nowrap text-16 font-medium text-sonnant-grey-6 opacity-95">
                  Program filters
                </div>
              </div>
            </Ripple>
          }
          open={isOpenFilter}
        >
          <SimplecastIntegrationFilter
            integration={integration}
            programIds={listProgramId}
            isLoading={isFetchingPing}
          />
        </CollapsibleList>

        <div tw="flex mt-5 mb-5 [> div]:(flex items-center text-15 font-medium text-sonnant-grey-6 space-x-3)!">
          <div>
            <span>Add to collection</span>
            <span>
              <CollectionDropdown
                defaultValue={integration.collectionId ?? ''}
                onChange={(value) => {
                  dispatch(
                    updateSingleSimplecastIntegration({
                      ...integration,
                      collectionOption: CollectionOptionEnum.COLLECTION,
                      collectionId: value,
                    }),
                  );
                }}
                menuPlacement="top"
                isMultiSelect={false}
              />
            </span>
          </div>
        </div>
      </div>

      {tier.isSubscriber && (
        <div
          className="btnReset"
          tw="w-full flex justify-end md-down:(flex-col)! w-[1rem]!"
        >
          <div>
            <Hint text="Remove integration" enterDelay={200}>
              <button
                type="button"
                className="success button apply"
                tw="bg-sonnant-red!"
                onClick={handleRemoveIntegration}
              >
                Remove
              </button>
            </Hint>
          </div>
        </div>
      )}

      <ConfirmModal
        ref={confirmRemoveModalRef}
        message={
          <div>
            <div>
              Removing an integration will stop any linked content items from
              sending updated data back to the source system. This is
              irreversible. <b>Would you like to proceed?</b>
            </div>
          </div>
        }
        title="Remove integration"
        confirmText="Confirm"
      />
    </div>
  );
};
